import { add, scale, viewDir } from "../../math.ts";
import { Btn } from "../../net/input.ts";
import { ActionKind, MoveKind, hv, setHv } from "../../sim/components.ts";
import { addOverhealth, aim, applyDamage, applyImpulse, applyStatus, coneConstructs, coneTargets, damageBarrier, hitscan, isEnemy, playersInRadius } from "../../sim/combat.ts";
import { damageConstruct } from "../../sim/constructs.ts";
import { setForm } from "../../sim/sim.ts";
import { HeroId, Role, Slot, type HeroDef } from "../types.ts";
import { fxk, pk, type HeroModule } from "../module.ts";
import { action, commonInput, consume, contactEnemies, launchProjectile, ownerAlive, ready, secs, simpleProjectile, startMove, trigger } from "../kitlib.ts";

export const V = { leap: 0, monsterTimer: 1 } as const;
export const PK = { Gamma: pk(HeroId.Bulk, 0), Lockdown: pk(HeroId.Bulk, 1), Grenade: pk(HeroId.Bulk, 2) } as const;
export const FX = { Gamma: fxk(HeroId.Bulk, 0), Slam: fxk(HeroId.Bulk, 1), Transform: fxk(HeroId.Bulk, 2) } as const;

const FORM = { banner: 0, hero: 1, monster: 2 } as const;

const def: HeroDef = {
  id: HeroId.Bulk,
  slug: "the-bulk",
  name: "The Bulk",
  role: Role.Tank,
  color: "#b8ff8f",
  blurb: "A mild scientist becomes a hero brute, then an unstoppable monster, trading precision for raw force.",
  health: 200,
  armor: 0,
  shields: 0,
  radius: 0.32,
  height: 1.8,
  headRadius: 0.24,
  speed: 6,
  jumpSpeed: 7.45,
  ammo: 25,
  reloadTime: 1.6,
  ultCost: 600,
  ultGain: { damage: 1.5, passive: 10 },
  abilities: {
    [Slot.Primary]: { name: "Ray Pointer", key: "LMB", description: "Fire a precise gamma ray sidearm.", cooldown: 0 },
    [Slot.Ability1]: { name: "Lab Lob", key: "Shift", description: "Throw a bouncing gamma grenade that bursts on impact.", cooldown: 8 },
    [Slot.Ultimate]: { name: "Bulk Up", key: "Q", description: "Transform into The Bulk at full health.", cooldown: 0 },
  },
  passive: { name: "Unstable Equation", description: "Ultimate changes Bryce into stronger Bulk forms while preserving cooldowns." },
  forms: [
    {
      name: "The Bulk",
      color: "#57b346",
      health: 400,
      shields: 300,
      radius: 0.62,
      height: 2.45,
      headRadius: 0.32,
      speed: 6,
      jumpSpeed: 7.45,
      ammo: 0,
      reloadTime: 0,
      ultCost: 3700,
      ultGain: { damage: 0.55, passive: 11 },
      abilities: {
        [Slot.Primary]: { name: "Heavy Hands", key: "LMB", description: "Punch enemies at close range.", cooldown: 0 },
        [Slot.Secondary]: { name: "Gamma Belch", key: "RMB", description: "Fire one of two heavy gamma projectiles.", cooldown: 6, charges: 2 },
        [Slot.Ability1]: { name: "Borrowed Courage", key: "Shift", description: "Shield yourself and nearby allies with gamma overhealth for 2.5 seconds.", cooldown: 10 },
        [Slot.Ability2]: { name: "Noisy Quarantine", key: "E", description: "Phase-lock an enemy for 2 seconds.", cooldown: 15 },
        [Slot.Ultimate]: { name: "Monster Bulk", key: "Q", description: "Transform into Monster Bulk for 12 seconds.", cooldown: 0 },
      },
      passive: { name: "Big Feet", description: "Press Jump from the ground to leap and slam on landing." },
    },
    {
      name: "Monster Bulk",
      color: "#69e65a",
      health: 1500,
      shields: 0,
      radius: 0.82,
      height: 3.05,
      headRadius: 0.42,
      speed: 6,
      jumpSpeed: 7.45,
      ammo: 0,
      reloadTime: 0,
      ultCost: 3700,
      ultGain: { damage: 0, passive: 12 },
      abilities: {
        [Slot.Primary]: { name: "Monster Hands", key: "LMB", description: "Swing huge fists for heavy melee damage.", cooldown: 0 },
        [Slot.Secondary]: { name: "Gamma Boulder", key: "RMB", description: "Fire a larger gamma projectile.", cooldown: 1 },
        [Slot.Ability1]: { name: "Monster Guard", key: "Shift", description: "Grant stronger gamma overhealth to nearby allies.", cooldown: 10 },
        [Slot.Ability2]: { name: "Monster Quarantine", key: "E", description: "Launch a larger phase-lock projectile.", cooldown: 5 },
        [Slot.Ultimate]: { name: "Worldbreaker Grip", key: "Q", description: "Smash one enemy in front with five heavy hits and stun them.", cooldown: 0 },
      },
      passive: { name: "Out of Control", description: "Unstoppable monster form lasts 12 seconds, then returns to The Bulk." },
    },
  ],
};

const currentForm = (sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number) => sim.c.Player.form[eid]!;

function transform(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number, form: number, hp: "frac" | "full" | "keep" = "full") {
  setForm(sim, eid, form, { hp });
  sim.emit({ type: "explosion", pos: add(sim.posOf(eid), { x: 0, y: 1, z: 0 }), radius: form === FORM.monster ? 6 : 3, kind: FX.Transform });
}

function punch(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number, yaw: number, monster: boolean) {
  const range = monster ? 5 : 4.5;
  const damage = monster ? 70 : 45;
  for (const t of coneTargets(sim, eid, range, 0.8, (e) => isEnemy(sim, eid, e))) {
    applyDamage(sim, t, damage, eid, { pos: add(sim.posOf(t), { x: 0, y: 1.2, z: 0 }), info: { kind: "melee" } });
    if (monster) applyImpulse(sim, t, { ...scale(viewDir(yaw, 0), 5), y: 5 });
  }
  for (const { eid: t } of coneConstructs(sim, eid, range, 0.8)) damageConstruct(sim, t, damage, eid);
}

function leap(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number, inputYaw: number, monster: boolean) {
  const d = viewDir(inputYaw, -0.18);
  startMove(sim, eid, MoveKind.Launch, secs(0.65), 24, { x: d.x * 16, y: 13, z: d.z * 16 });
  setHv(sim.c, eid, V.leap, monster ? 2 : 1);
}

const module: HeroModule = {
  def,
  kit: {
    init(sim, eid) {
      setHv(sim.c, eid, V.leap, 0);
      setHv(sim.c, eid, V.monsterTimer, 0);
    },
    tick(sim, eid, input, pressed, canAct) {
      const c = sim.c;
      const form = currentForm(sim, eid);
      const monster = form === FORM.monster;
      const timer = hv(c, eid, V.monsterTimer);
      if (timer > 0) {
        setHv(c, eid, V.monsterTimer, timer - 1);
        if (timer <= 1 && monster) transform(sim, eid, FORM.hero, "frac");
      }

      if (c.Move.kind[eid] === MoveKind.Launch && hv(c, eid, V.leap) > 0) {
        contactEnemies(sim, eid, monster ? 1.6 : 1.25, (t) => {
          applyDamage(sim, t, monster ? 50 : 35, eid, { info: { kind: "melee" } });
          applyStatus(sim, t, "knockdown", monster ? 0.7 : 0.4, { source: eid });
        });
      } else if (hv(c, eid, V.leap) > 0 && c.Player.grounded[eid]) {
        setHv(c, eid, V.leap, 0);
        sim.emit({ type: "explosion", pos: sim.posOf(eid), radius: monster ? 5 : 3.5, kind: FX.Slam });
        for (const { eid: t } of playersInRadius(sim, sim.posOf(eid), monster ? 5 : 3.5, (e) => isEnemy(sim, eid, e), true))
          applyDamage(sim, t, monster ? 50 : 25, eid, { info: { kind: "explosion" } });
      }

      commonInput(sim, eid, input, pressed);
      if (!canAct) return;

      if (pressed & Btn.Ultimate) {
        if (form === FORM.banner) {
          transform(sim, eid, FORM.hero, "full");
          applyStatus(sim, eid, "resist", 3, { source: eid });
          action(sim, eid, ActionKind.Ult, 45);
        } else if (form === FORM.hero && c.Ult.charge[eid]! >= 1) {
          c.Ult.charge[eid] = 0;
          transform(sim, eid, FORM.monster, "full");
          setHv(c, eid, V.monsterTimer, secs(12));
          applyStatus(sim, eid, "unstoppable", 12, { source: eid });
          action(sim, eid, ActionKind.Ult, 60);
        } else if (monster) {
          for (const t of coneTargets(sim, eid, 5, 0.55, (e) => isEnemy(sim, eid, e))) {
            applyDamage(sim, t, 200, eid, { pos: add(sim.posOf(t), { x: 0, y: 1.2, z: 0 }), info: { kind: "melee" } });
            applyStatus(sim, t, "stun", 5, { source: eid });
            break;
          }
          applyStatus(sim, eid, "resist", 50 / 60, { resistPct: 0.3, source: eid });
          action(sim, eid, ActionKind.Ult, 50);
        }
      }

      if (form === FORM.banner) {
        if ((pressed & Btn.Ability1) && ready(sim, eid, Slot.Ability1)) {
          consume(sim, eid, Slot.Ability1);
          launchProjectile(sim, eid, PK.Grenade, 28, input, { radius: 0.5, gravity: 12, seconds: 2 });
          action(sim, eid, ActionKind.Cast, 16);
        }
        if (trigger(sim, eid, !!(input.buttons & Btn.Primary))) {
          c.Weapon.fireCd[eid] = 12;
          action(sim, eid, ActionKind.Fire, 6);
          const a = aim(sim, eid);
          hitscan(sim, eid, a.origin, a.dir, { range: 60, damage: 16, canCrit: true, fxKind: FX.Gamma });
        }
        return;
      }

      if ((pressed & Btn.Jump) && c.Player.grounded[eid]) leap(sim, eid, input.yaw, monster);
      if ((pressed & Btn.Secondary) && ready(sim, eid, Slot.Secondary)) {
        consume(sim, eid, Slot.Secondary);
        launchProjectile(sim, eid, PK.Gamma, monster ? 60 : 80, input, { radius: monster ? 1.5 : 0.5, seconds: 1.1 });
        action(sim, eid, ActionKind.AltFire, 18);
      }
      if ((pressed & Btn.Ability2) && ready(sim, eid, Slot.Ability2)) {
        consume(sim, eid, Slot.Ability2);
        launchProjectile(sim, eid, PK.Lockdown, monster ? 150 : 100, input, { radius: monster ? 0.8 : 0.3, seconds: 0.6 });
        action(sim, eid, ActionKind.Cast, 18);
      }
      if ((pressed & Btn.Ability1) && ready(sim, eid, Slot.Ability1)) {
        consume(sim, eid, Slot.Ability1);
        const guardRadius = monster ? 6 : 5;
        const guardTicks = secs(monster ? 4 : 2.5);
        for (const { eid: a } of playersInRadius(sim, sim.posOf(eid), guardRadius, (e) => !isEnemy(sim, eid, e), false)) addOverhealth(sim, a, monster ? 250 : a === eid ? 200 : 100, guardTicks, eid);
        action(sim, eid, ActionKind.Cast, 20);
      }
      if (trigger(sim, eid, !!(input.buttons & Btn.Primary), true)) {
        c.Weapon.fireCd[eid] = monster ? 31 : 28;
        action(sim, eid, ActionKind.Fire, 18);
        punch(sim, eid, input.yaw, monster);
      }
    },
  },
  projectiles: {
    [PK.Grenade]: simpleProjectile(
      (sim, _p, owner, hit) => {
        if (!ownerAlive(sim, owner)) return;
        sim.emit({ type: "explosion", pos: hit.pos, radius: 3, kind: FX.Gamma });
        for (const { eid: t } of playersInRadius(sim, hit.pos, 3, (e) => isEnemy(sim, owner, e), true)) {
          applyDamage(sim, t, 40, owner, { pos: hit.pos, info: { kind: "explosion" } });
          applyImpulse(sim, t, { x: 0, y: 6, z: 0 });
        }
      },
      { color: "#b8ff8f", size: 0.22, trail: true },
    ),
    [PK.Gamma]: simpleProjectile(
      (sim, _p, owner, hit) => {
        if (!ownerAlive(sim, owner)) return;
        const dmg = sim.c.Player.form[owner] === FORM.monster ? 125 : 65;
        if (hit.target !== undefined) applyDamage(sim, hit.target, dmg, owner, { crit: hit.head, pos: hit.pos, info: { kind: "projectile" } });
        else if (hit.construct !== undefined) damageConstruct(sim, hit.construct, dmg, owner);
        else if (hit.barrier !== undefined) damageBarrier(sim, hit.barrier, dmg, owner);
        sim.emit({ type: "impact", pos: hit.pos, kind: FX.Gamma });
      },
      { color: "#77ff55", size: 0.32, trail: true },
    ),
    [PK.Lockdown]: simpleProjectile(
      (sim, _p, owner, hit) => {
        if (!ownerAlive(sim, owner)) return;
        if (hit.target !== undefined) {
          applyDamage(sim, hit.target, 5, owner, { pos: hit.pos, info: { kind: "projectile" } });
          applyStatus(sim, hit.target, "phased", 2, { source: owner });
          applyStatus(sim, hit.target, "root", 2, { source: owner });
        } else if (hit.construct !== undefined) damageConstruct(sim, hit.construct, 5, owner);
        sim.emit({ type: "impact", pos: hit.pos, kind: FX.Gamma });
      },
      { color: "#eaffd0", size: 0.2, trail: true },
    ),
  },
  fx: {
    [FX.Gamma]: { color: "#77ff55" },
    [FX.Slam]: { color: "#57b346" },
    [FX.Transform]: { color: "#b8ff8f" },
  },
};

export default module;
